import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Bookmark } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";

// Only two surahs have a Tafsir Explorer structure at all (app-wide v1
// scope), so checking both is exhaustive, not an arbitrary cap.
const SURAHS = [1, 2];

/* The only place a user can explicitly "bookmark" something today is a
   Tadabbur section (TafsirReader's Bookmark button, toggling
   tafsir_progress.bookmarked) — so "your bookmarks" means every section
   across both surahs with that flag set, not recent-activity history
   (that used to live here as a single quick-resume link; this replaces it
   with an actual list). Cached module-wide since the icon can mount twice
   (mobile + desktop top bars). */
let _bookmarksPromise = null;
function fetchBookmarks() {
  if (_bookmarksPromise) return _bookmarksPromise;
  _bookmarksPromise = Promise.all(
    SURAHS.map((n) =>
      Promise.all([
        api.get(`/tafsir/progress/${n}`).then((r) => r.data || []).catch(() => []),
        api.get(`/tafsir/surah/${n}`).then((r) => r.data).catch(() => null),
      ]).then(([progress, meta]) => {
        if (!meta) return [];
        const bySlug = Object.fromEntries((meta.sections || []).map((s) => [s.slug, s]));
        return progress
          .filter((p) => p.bookmarked && bySlug[p.section])
          .map((p) => {
            const s = bySlug[p.section];
            return {
              surah: n,
              surahName: meta.name_en,
              slug: p.section,
              title: s.title,
              start: s.start,
              end: s.end,
            };
          });
      }),
    ),
  ).then((lists) => lists.flat());
  return _bookmarksPromise;
}

export default function BookmarkIcon() {
  const { tx, t } = useLanguage();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    let alive = true;
    fetchBookmarks().then((list) => { if (alive) setItems(list); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="relative inline-flex items-center justify-center h-8 w-8 rounded-lg border border-sand-300 text-gold-600 hover:bg-sand-200 transition"
        aria-label={items.length ? t("bookmarkIcon.bookmarks", { itemsCount: items.length }) : t("bookmarkIcon.bookmarks2")}
        data-testid="bookmark-icon"
      >
        <Bookmark className="h-4 w-4" />
        {items.length > 0 && (
          <span
            className="absolute -top-1.5 -right-1.5 min-w-[16px] h-[16px] px-1 rounded-full bg-gold-500 text-white text-[9px] font-semibold grid place-items-center"
            data-testid="bookmark-count"
          >
            {items.length > 9 ? "9+" : items.length}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-72 max-w-[min(85vw,calc(var(--app-frame-w)_-_2rem))] rounded-xl border border-sand-300 bg-white shadow-xl z-50 overflow-hidden"
          data-testid="bookmark-panel"
        >
          <div className="px-4 py-3 border-b border-sand-200">
            <span className="font-display font-semibold text-forest-700 text-sm">{t("bookmarkIcon.bookmarks2")}</span>
          </div>
          {items.length === 0 ? (
            <p className="px-4 py-6 text-sm text-muted-foreground">
              {t("bookmarkIcon.noBookmarksYetTapBookmark")}
            </p>
          ) : (
            <div className="max-h-80 overflow-y-auto divide-y divide-sand-200">
              {items.map((b) => (
                <Link
                  key={`${b.surah}-${b.slug}`}
                  to={`/app/tadabbur/${b.surah}?section=${b.slug}`}
                  onClick={() => setOpen(false)}
                  className="block px-4 py-3 hover:bg-sand-100 transition"
                  data-testid={`bookmark-item-${b.surah}-${b.slug}`}
                >
                  <div className="text-sm font-medium text-forest-700 truncate">{tx(b.title)}</div>
                  <div className="text-xs text-muted-foreground">{b.surahName} · {b.start === b.end ? t("common.verseN", { n: b.start }) : t("common.versesRange", { start: b.start, end: b.end })}</div>
                </Link>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
